import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  Agent,
  AgentIdentity,
  AgentRequest,
  Capability,
  CapabilityModel,
  CapabilityModelRequest,
  Channel,
  KnowledgeDocument,
  KnowledgeTextRequest,
  KnowledgeUpdateRequest,
  normalizeCapabilityModel,
  ChannelRequest,
  Conversation,
  ConversationMessage,
  LlmModel,
  ModelRequest,
  PageResponse,
  Playbook,
  PlaybookRequest,
  ProjectOption,
  ProjectPlan,
  ProjectPlanRequest,
  ServiceAccount,
  ToolConnection,
  AgentTrigger,
  normalizeTrigger,
  TriggerRun,
  Provider,
  ProviderRequest,
} from '../models/ai-agents.models';

/** Base URL of the AI Agents Service system API, e.g. https://host/genai-mic/agent/v1/system */
export function aiAgentsSystemBaseUrl(): string {
  const root = environment.agentapiURL.endsWith('/') ? environment.agentapiURL : environment.agentapiURL + '/';
  return root + environment.path.agentplatform;
}

/** Client for the system scope of the AI Agents Service (SUPER_ADMIN). */
@Injectable({ providedIn: 'root' })
export class AiAgentsApiService {
  readonly baseUrl = aiAgentsSystemBaseUrl();
  private readonly projectBaseUrl = environment.projectapiUrl + '/genai-mic/project/v1/system';

  constructor(private http: HttpClient) {}

  // ------------------------------------------------------------ providers

  listProviders(): Observable<Provider[]> {
    return this.http.get<Provider[]>(`${this.baseUrl}/providers`);
  }

  getProvider(id: string): Observable<Provider> {
    return this.http.get<Provider>(`${this.baseUrl}/providers/${enc(id)}`);
  }

  createProvider(body: ProviderRequest): Observable<Provider> {
    return this.http.post<Provider>(`${this.baseUrl}/providers`, body);
  }

  updateProvider(id: string, body: ProviderRequest): Observable<Provider> {
    return this.http.put<Provider>(`${this.baseUrl}/providers/${enc(id)}`, body);
  }

  /** Write-only: the response carries hasApiKey, never the key. */
  setProviderApiKey(id: string, apiKey: string): Observable<Provider> {
    return this.http.put<Provider>(`${this.baseUrl}/providers/${enc(id)}/api-key`, { apiKey });
  }

  deleteProvider(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/providers/${enc(id)}`);
  }

  // ------------------------------------------------------------ models

  listModels(providerId?: string | null): Observable<LlmModel[]> {
    let params = new HttpParams();
    if (providerId) {
      params = params.set('providerId', providerId);
    }
    return this.http.get<LlmModel[]>(`${this.baseUrl}/models`, { params });
  }

  getModel(id: string): Observable<LlmModel> {
    return this.http.get<LlmModel>(`${this.baseUrl}/models/${enc(id)}`);
  }

  createModel(body: ModelRequest): Observable<LlmModel> {
    return this.http.post<LlmModel>(`${this.baseUrl}/models`, body);
  }

  updateModel(id: string, body: ModelRequest): Observable<LlmModel> {
    return this.http.put<LlmModel>(`${this.baseUrl}/models/${enc(id)}`, body);
  }

  setModelEnabled(id: string, enabled: boolean): Observable<LlmModel> {
    return this.updateModel(id, { enabled });
  }

  deleteModel(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/models/${enc(id)}`);
  }

  // ------------------------------------------------------------ agents

  listAgents(projectId?: string | null): Observable<Agent[]> {
    let params = new HttpParams();
    if (projectId) {
      params = params.set('projectId', projectId);
    }
    return this.http.get<Agent[]>(`${this.baseUrl}/agents`, { params });
  }

  getAgent(id: string): Observable<Agent> {
    return this.http.get<Agent>(`${this.baseUrl}/agents/${enc(id)}`);
  }

  createAgent(body: AgentRequest): Observable<Agent> {
    return this.http.post<Agent>(`${this.baseUrl}/agents`, body);
  }

  updateAgent(id: string, body: AgentRequest): Observable<Agent> {
    return this.http.put<Agent>(`${this.baseUrl}/agents/${enc(id)}`, body);
  }

  deleteAgent(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/agents/${enc(id)}`);
  }

  /** Multipart field "file": PNG, JPEG or WebP up to 2 MB. Answers the agent with its new avatarUrl. */
  uploadAvatar(agentId: string, file: Blob, fileName?: string): Observable<Agent> {
    const form = new FormData();
    form.append('file', file, fileName || (file instanceof File ? file.name : 'avatar'));
    return this.http.post<Agent>(`${this.baseUrl}/agents/${enc(agentId)}/avatar`, form);
  }

  /** Removes the picture; the uploaded file is deleted. */
  removeAvatar(agentId: string): Observable<Agent> {
    return this.http.delete<Agent>(`${this.baseUrl}/agents/${enc(agentId)}/avatar`);
  }

  getIdentity(agentId: string): Observable<AgentIdentity> {
    return this.http.get<AgentIdentity>(`${this.baseUrl}/agents/${enc(agentId)}/identity`);
  }

  putIdentity(agentId: string, body: AgentIdentity): Observable<AgentIdentity> {
    return this.http.put<AgentIdentity>(`${this.baseUrl}/agents/${enc(agentId)}/identity`, body);
  }

  // ------------------------------------------------------------ channels

  listChannels(agentId: string): Observable<Channel[]> {
    return this.http.get<Channel[]>(`${this.baseUrl}/agents/${enc(agentId)}/channels`);
  }

  getChannel(agentId: string, channelId: string): Observable<Channel> {
    return this.http.get<Channel>(`${this.baseUrl}/agents/${enc(agentId)}/channels/${enc(channelId)}`);
  }

  createChannel(agentId: string, body: ChannelRequest): Observable<Channel> {
    return this.http.post<Channel>(`${this.baseUrl}/agents/${enc(agentId)}/channels`, body);
  }

  updateChannel(agentId: string, channelId: string, body: ChannelRequest): Observable<Channel> {
    return this.http.put<Channel>(`${this.baseUrl}/agents/${enc(agentId)}/channels/${enc(channelId)}`, body);
  }

  deleteChannel(agentId: string, channelId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/agents/${enc(agentId)}/channels/${enc(channelId)}`);
  }

  // ------------------------------------------------------------ conversations

  listConversations(filter: {
    projectId?: string | null;
    agentId?: string | null;
    page?: number;
    size?: number;
  }): Observable<PageResponse<Conversation>> {
    let params = new HttpParams()
      .set('page', String(filter.page ?? 0))
      .set('size', String(filter.size ?? 20));
    if (filter.projectId) {
      params = params.set('projectId', filter.projectId);
    }
    if (filter.agentId) {
      params = params.set('agentId', filter.agentId);
    }
    return this.http.get<PageResponse<Conversation>>(`${this.baseUrl}/conversations`, { params });
  }

  getConversation(id: string): Observable<Conversation> {
    return this.http.get<Conversation>(`${this.baseUrl}/conversations/${enc(id)}`);
  }

  getConversationMessages(id: string): Observable<ConversationMessage[]> {
    return this.http.get<ConversationMessage[]>(`${this.baseUrl}/conversations/${enc(id)}/messages`);
  }

  // ------------------------------------------------------------ project plans

  listProjectPlans(): Observable<ProjectPlan[]> {
    return this.http.get<ProjectPlan[]>(`${this.baseUrl}/project-plans`);
  }

  getProjectPlan(projectId: string): Observable<ProjectPlan> {
    return this.http.get<ProjectPlan>(`${this.baseUrl}/project-plans/${enc(projectId)}`);
  }

  /** allowedModelIds null = every enabled model. */
  updateProjectPlan(projectId: string, body: ProjectPlanRequest): Observable<ProjectPlan> {
    return this.http.put<ProjectPlan>(`${this.baseUrl}/project-plans/${enc(projectId)}`, body);
  }

  // ------------------------------------------------------------ capability models (contract v1.2)

  listCapabilityModels(capability?: Capability | null): Observable<CapabilityModel[]> {
    let params = new HttpParams();
    if (capability) {
      params = params.set('capability', capability);
    }
    return this.http
      .get<unknown[]>(`${this.baseUrl}/capability-models`, { params })
      .pipe(map((list) => (list || []).map(normalizeCapabilityModel)));
  }

  createCapabilityModel(body: CapabilityModelRequest): Observable<CapabilityModel> {
    return this.http.post<unknown>(`${this.baseUrl}/capability-models`, body).pipe(map(normalizeCapabilityModel));
  }

  updateCapabilityModel(id: string, body: CapabilityModelRequest): Observable<CapabilityModel> {
    return this.http.put<unknown>(`${this.baseUrl}/capability-models/${enc(id)}`, body).pipe(map(normalizeCapabilityModel));
  }

  deleteCapabilityModel(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/capability-models/${enc(id)}`);
  }

  // ------------------------------------------------------------ attachments (contract v1.2)

  /** The image or recording of a message, for the conversation view (permission conversation.get). */
  getSystemAttachment(id: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/attachments/${enc(id)}`, { responseType: 'blob' });
  }

  // ------------------------------------------------------------ knowledge files (support, contract v1.2)

  /** Without text; with status and chunkCount. */
  listKnowledge(agentId: string): Observable<KnowledgeDocument[]> {
    return this.http.get<KnowledgeDocument[]>(`${this.baseUrl}/agents/${enc(agentId)}/knowledge`);
  }

  getKnowledge(agentId: string, docId: string): Observable<KnowledgeDocument> {
    return this.http.get<KnowledgeDocument>(`${this.baseUrl}/agents/${enc(agentId)}/knowledge/${enc(docId)}`);
  }

  createKnowledgeText(agentId: string, body: KnowledgeTextRequest): Observable<KnowledgeDocument> {
    return this.http.post<KnowledgeDocument>(`${this.baseUrl}/agents/${enc(agentId)}/knowledge/text`, body);
  }

  /** Multipart "file" (txt, md, pdf, docx up to 10 MB) and optional "title". */
  uploadKnowledgeFile(agentId: string, file: Blob, title?: string | null, fileName?: string): Observable<KnowledgeDocument> {
    const form = new FormData();
    form.append('file', file, fileName || (file instanceof File ? file.name : 'knowledge'));
    if (title) {
      form.append('title', title);
    }
    return this.http.post<KnowledgeDocument>(`${this.baseUrl}/agents/${enc(agentId)}/knowledge/files`, form);
  }

  updateKnowledge(agentId: string, docId: string, body: KnowledgeUpdateRequest): Observable<KnowledgeDocument> {
    return this.http.put<KnowledgeDocument>(`${this.baseUrl}/agents/${enc(agentId)}/knowledge/${enc(docId)}`, body);
  }

  deleteKnowledge(agentId: string, docId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/agents/${enc(agentId)}/knowledge/${enc(docId)}`);
  }

  reindexKnowledge(agentId: string, docId: string): Observable<KnowledgeDocument> {
    return this.http.post<KnowledgeDocument>(`${this.baseUrl}/agents/${enc(agentId)}/knowledge/${enc(docId)}/reindex`, {});
  }

  // ------------------------------------------------------------ playbooks (support, contract v1.4; no plan checks)

  /** sortOrder then createdAt; without content. */
  listPlaybooks(agentId: string): Observable<Playbook[]> {
    return this.http.get<Playbook[]>(`${this.baseUrl}/agents/${enc(agentId)}/playbooks`);
  }

  /** One playbook with its content. */
  getPlaybook(agentId: string, playbookId: string): Observable<Playbook> {
    return this.http.get<Playbook>(`${this.baseUrl}/agents/${enc(agentId)}/playbooks/${enc(playbookId)}`);
  }

  /** 201; a missing key is made from the title. */
  createPlaybook(agentId: string, body: PlaybookRequest): Observable<Playbook> {
    return this.http.post<Playbook>(`${this.baseUrl}/agents/${enc(agentId)}/playbooks`, body);
  }

  /** null fields stay unchanged. */
  updatePlaybook(agentId: string, playbookId: string, body: PlaybookRequest): Observable<Playbook> {
    return this.http.put<Playbook>(`${this.baseUrl}/agents/${enc(agentId)}/playbooks/${enc(playbookId)}`, body);
  }

  setPlaybookEnabled(agentId: string, playbookId: string, enabled: boolean): Observable<Playbook> {
    return this.updatePlaybook(agentId, playbookId, { enabled });
  }

  /** 204. */
  deletePlaybook(agentId: string, playbookId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/agents/${enc(agentId)}/playbooks/${enc(playbookId)}`);
  }

  // ------------------------------------------------------------ AI employee and tools (support, contract v1.5; read-only)

  /** The agent's AI employee: name, key prefix, connected at, valid; never the key. */
  getAgentServiceAccount(agentId: string): Observable<ServiceAccount> {
    return this.http.get<ServiceAccount>(`${this.baseUrl}/agents/${enc(agentId)}/service-account`);
  }

  /** Every configured MCP service with the agent's connection and the tools its AI employee may see. */
  listAgentTools(agentId: string): Observable<ToolConnection[]> {
    return this.http.get<ToolConnection[]>(`${this.baseUrl}/agents/${enc(agentId)}/tools`);
  }

  // ------------------------------------------------------------ automations / triggers (support, contract v1.6; read-only)

  /** The agent's triggers (schedule, prompt, tools, next run, last status). */
  listAgentTriggers(agentId: string): Observable<AgentTrigger[]> {
    return this.http
      .get<any[]>(`${this.baseUrl}/agents/${enc(agentId)}/triggers`)
      .pipe(map((list) => (Array.isArray(list) ? list.map(normalizeTrigger) : [])));
  }

  /** One trigger (GET /system/agents/{id}/triggers/{triggerId}). */
  getAgentTrigger(agentId: string, triggerId: string): Observable<AgentTrigger> {
    return this.http.get<any>(`${this.baseUrl}/agents/${enc(agentId)}/triggers/${enc(triggerId)}`).pipe(map(normalizeTrigger));
  }

  /** One page of a trigger's runs, newest first by seq: { items, total, page, size } (the service caps size at 100). */
  listTriggerRuns(agentId: string, triggerId: string, page = 0, size = 20): Observable<PageResponse<TriggerRun>> {
    const params = new HttpParams().set('page', String(page)).set('size', String(size));
    return this.http.get<PageResponse<TriggerRun>>(`${this.baseUrl}/agents/${enc(agentId)}/triggers/${enc(triggerId)}/runs`, { params });
  }

  // ------------------------------------------------------------ projects (project service, for pickers)

  listProjects(): Observable<ProjectOption[]> {
    const params = new HttpParams().set('page', '0').set('size', '500');
    return this.http
      .get<{ content?: Array<{ projectId: string; domain?: string; name?: string }> }>(
        `${this.projectBaseUrl}/list/projects`,
        { params }
      )
      .pipe(
        map((res) =>
          (res?.content || []).map((p) => ({ projectId: p.projectId, name: p.domain || p.name || p.projectId }))
        )
      );
  }
}

function enc(value: string): string {
  return encodeURIComponent(value);
}
